import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import api from "../../lib/api";
import useFocusTrap from "../../hooks/useFocusTrap";
import { buildVaSiteLaunchUrl } from "../../utils/vaLaunchSites";
import { POINT_OF_CARE_AVAILABILITY_EVENT } from "../Tutorial/utils/gatedSlides";

const getSecondarySummary = (site) => {
  const secondarySites = site?.secondarySites || [];
  if (secondarySites.length === 0) {
    return "No secondary sites listed.";
  }

  return secondarySites.map((secondary) => `${secondary.city}, ${secondary.state}`).join("; ");
};

const normalizeStationId = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

export default function PointOfCareLaunchSelector({ currentSta3n, fhirServer, embedded = false }) {
  const [launchSites, setLaunchSites] = useState([]);
  const [selectedSiteKey, setSelectedSiteKey] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [derivedFhirServer, setDerivedFhirServer] = useState("");
  const [storedSta3n, setStoredSta3n] = useState("");
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useFocusTrap(showModal, dialogRef, null, () => setShowModal(false));

  // The training guide walks through this window, so it opens and closes it
  // directly. A synthetic click on the button is not dependable while the
  // guide has the page under its interaction lock.
  useEffect(() => {
    const open = () => setShowModal(true);
    const close = () => setShowModal(false);
    window.addEventListener("tutorial-open-point-of-care", open);
    window.addEventListener("tutorial-close-point-of-care", close);
    return () => {
      window.removeEventListener("tutorial-open-point-of-care", open);
      window.removeEventListener("tutorial-close-point-of-care", close);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchLaunchSites = async () => {
      setIsLoading(true);
      setErrorMessage("");
      let loadedSiteCount = 0;
      try {
        const response = await api.get("/auth/va/launch-sites");
        const sites = Array.isArray(response?.launchSites) ? response.launchSites : [];
        loadedSiteCount = sites.length;

        if (!isMounted) {
          return;
        }

        setLaunchSites(sites);
        setDerivedFhirServer(response?.fhirServer || "");
        setStoredSta3n(response?.sta3n || "");
        setSelectedSiteKey((current) => {
          if (current && sites.some((site) => `${site.sta3n}|${site.duz}` === current)) {
            return current;
          }

          const effectiveCurrentSta3n = normalizeStationId(currentSta3n || response?.sta3n || "");
          const currentSite = sites.find((site) => site.sta3n === effectiveCurrentSta3n);
          const fallbackSite = currentSite || sites[0];
          return fallbackSite ? `${fallbackSite.sta3n}|${fallbackSite.duz}` : "";
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }
        console.error("Failed to load VA launch sites:", error);
        Sentry.captureException(error, {
          tags: { flow: "vaSiteSwap", step: "fetchLaunchSites" },
        });
        setErrorMessage("Point of Care sites could not be loaded.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
          window.dispatchEvent(
            new CustomEvent(POINT_OF_CARE_AVAILABILITY_EVENT, {
              detail: { available: loadedSiteCount > 0 },
            })
          );
        }
      }
    };

    fetchLaunchSites();

    return () => {
      isMounted = false;
    };
  }, [currentSta3n]);

  const selectedSite = useMemo(
    () => launchSites.find((site) => `${site.sta3n}|${site.duz}` === selectedSiteKey) || null,
    [launchSites, selectedSiteKey]
  );
  const normalizedCurrentSta3n = normalizeStationId(currentSta3n || storedSta3n);
  const hasCurrentSta3n = normalizedCurrentSta3n.length > 0;
  const isSelectedCurrentSta3n = Boolean(
    selectedSite && hasCurrentSta3n && selectedSite.sta3n === normalizedCurrentSta3n
  );
  const resolvedFhirServer = String(fhirServer || derivedFhirServer || "").trim();
  const hasFhirServer = resolvedFhirServer.length > 0;
  const canLaunchSelectedSite = Boolean(selectedSite && !isSelectedCurrentSta3n && hasFhirServer);

  const currentSite = hasCurrentSta3n
    ? launchSites.find((site) => site.sta3n === normalizedCurrentSta3n) || null
    : null;
  const currentDisplayName =
    currentSite?.displayName ||
    (launchSites.length === 0 ? "No supported launch sites" : "Select launch site");

  const handleLaunch = (event) => {
    event.preventDefault();

    if (!selectedSite) {
      toast.error("Please select a Point of Care site.");
      return;
    }

    if (isSelectedCurrentSta3n) {
      toast.info("You are already launched from this Point of Care site.");
      return;
    }

    if (!hasFhirServer) {
      toast.error("A VA FHIR server is required to launch a Point of Care site.");
      return;
    }

    try {
      const launchUrl = buildVaSiteLaunchUrl({
        fhirServer: resolvedFhirServer,
        sta3n: selectedSite.sta3n,
        duz: selectedSite.duz,
      });
      window.location.assign(launchUrl);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { flow: "vaSiteSwap", step: "launch" },
        extra: {
          sta3n: selectedSite.sta3n,
        },
      });
      toast.error(error.message || "Unable to launch selected Point of Care site.");
    }
  };

  if (isLoading || launchSites.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={
          embedded
            ? "mt-3 border-t border-sky-200 pt-3"
            : "mt-1 mb-2 mr-3 w-fit max-w-full rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 px-3.5 py-2 shadow-sm"
        }
      >
        <div
          className={
            embedded
              ? "grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-4"
              : "flex flex-wrap items-center gap-2 sm:gap-3"
          }
        >
          <div className="min-w-0">
            <h2
              className={`text-xs font-semibold uppercase tracking-wide ${
                embedded ? "text-sky-700" : "text-indigo-700"
              }`}
            >
              Point of Care
            </h2>
            <p
              className={`text-base font-semibold break-words ${
                embedded ? "text-sky-900" : "text-indigo-950"
              }`}
            >
              {isLoading ? "Loading site..." : currentDisplayName}
            </p>
            {errorMessage && <p className="text-xs text-red-700">{errorMessage}</p>}
          </div>
          <button
            type="button"
            id="change_site_button"
            onClick={() => setShowModal(true)}
            disabled={isLoading || launchSites.length === 0}
            className={`w-full shrink-0 rounded-lg border bg-white px-3 py-1.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto ${
              embedded
                ? "border-sky-300 text-sky-700 hover:bg-sky-100"
                : "border-indigo-300 text-indigo-700 hover:bg-indigo-100"
            }`}
          >
            Change site
          </button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f172a]/40 px-4">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="point-of-care-title"
            className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold tracking-wide text-indigo-700">
                  Point of Care
                </p>
                <h2 id="point-of-care-title" className="mt-2 text-xl font-bold text-slate-900">
                  Select launch site
                </h2>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-md border border-slate-200 px-2 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                aria-label="Close point of care selector"
              >
                X
              </button>
            </div>

            <form onSubmit={handleLaunch} className="mt-4">
              <label
                htmlFor="point-of-care-site"
                className="block text-sm font-semibold text-slate-800"
              >
                Launch from
              </label>
              <select
                id="point-of-care-site"
                value={selectedSiteKey}
                onChange={(event) => setSelectedSiteKey(event.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              >
                {launchSites.map((site) => {
                  const key = `${site.sta3n}|${site.duz}`;
                  return (
                    <option key={key} value={key}>
                      {site.facilityName} — {site.displayName} ({site.sta3n})
                    </option>
                  );
                })}
              </select>

              {selectedSite && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <p className="font-semibold text-slate-900">{selectedSite.facilityName}</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Station {selectedSite.sta3n}
                    {selectedSite.visn ? ` · VISN ${selectedSite.visn}` : ""}
                    {selectedSite.classification ? ` · ${selectedSite.classification}` : ""}
                  </p>
                  {isSelectedCurrentSta3n && (
                    <p className="mt-2 text-xs font-semibold text-slate-700">
                      You are currently launched from this site.
                    </p>
                  )}
                  {!hasFhirServer && (
                    <p className="mt-2 text-xs font-semibold text-amber-700">
                      A VA FHIR server is required before this site can be launched.
                    </p>
                  )}
                  <div className="mt-2 text-xs text-slate-600">
                    <p className="font-semibold" title={getSecondarySummary(selectedSite)}>
                      Included secondary sites ({selectedSite.secondarySites?.length || 0})
                    </p>
                    {selectedSite.secondarySites?.length > 0 ? (
                      <ul className="mt-1 max-h-36 space-y-1 overflow-y-auto pr-1">
                        {selectedSite.secondarySites.map((secondary) => (
                          <li key={secondary.stationId}>
                            {secondary.facilityName} — {secondary.city}, {secondary.state} (
                            {secondary.stationId})
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-1">No secondary sites listed.</p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canLaunchSelectedSite}
                  className="rounded-md bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Launch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
